import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import resume1 from "../assets/resumee1.png";
import resume2 from "../assets/resumee2.png";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = [resume1, resume2];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <Box
      component="section"
      aria-labelledby="hero-heading"
      itemScope
      itemType="https://schema.org/WebPage"
      sx={{
        px: { xs: 2, sm: 3, md: 8 },
        pt: { xs: 3, sm: 4, md: 6 },
        pb: { xs: 3, sm: 4, md: 6 },
        overflow: "hidden",
        minHeight: { xs: "auto", md: "90vh" },
        display: "flex",
        alignItems: "center",
        background: "linear-gradient(160deg,#fdf6ff 0%,#fff 60%)",
      }}
    >
      {/* ✅ SEO: JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "CreateFree Resume Builder",
            url: "https://www.createfreeresume.com",
            description:
              "Create a free professional resume online in minutes. ATS-friendly templates, no design skills needed.",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              reviewCount: "10000",
            },
          }),
        }}
      />

      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", mx: "auto" }}
      >
        {/* IMAGE */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
            pl: { md: 4 },
            order: { xs: 1, md: 2 },
            mb: { xs: 1.5, md: 0 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              height: { xs: "230px", sm: "310px", md: "450px" },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: { xs: "190px", sm: "270px", md: "400px" },
                height: { xs: "230px", sm: "310px", md: "400px" },
                perspective: "1000px",
              }}
              role="img"
              aria-label="Free resume builder – professional resume template preview"
            >
              {images.map((src, i) => (
                <Box
                  key={i}
                  component="img"
                  src={src}
                  alt={
                    i === 0
                      ? "Free ATS-friendly resume template – modern professional design by CreateFree Resume"
                      : "Free resume template – clean minimal layout for job seekers"
                  }
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchpriority={i === 0 ? "high" : "low"}
                  width="400"
                  height="400"
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: 3,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                    transform:
                      currentImage === i
                        ? "rotateY(0deg) scale(1)"
                        : "rotateY(180deg) scale(0.8)",
                    opacity: currentImage === i ? 1 : 0,
                    transition: "all 0.8s ease-in-out",
                    backfaceVisibility: "hidden",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        {/* TEXT */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            pr: { md: 4 },
            order: { xs: 2, md: 1 },
          }}
        >
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
              maxWidth: { xs: "100%", sm: "480px", md: "520px" },
              width: "100%",
            }}
          >
            {/* ✅ SEO: H1 keyword-rich heading — unchanged text */}
            <Typography
              id="hero-heading"
              component="h1"
              fontWeight="bold"
              gutterBottom
              itemProp="headline"
              sx={{
                fontSize: { xs: "1.7rem", sm: "2.2rem", md: "3rem" },
                lineHeight: 1.2,
                mb: { xs: 1.5, md: 2 },
                color: "#1a1a2e",
              }}
            >
              Build Your Dream Resume —{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Free & Fast!
              </Box>
            </Typography>

            {/* ✅ SEO: itemProp="description" + keyword strong tags */}
            <Typography
              component="p"
              color="text.secondary"
              paragraph
              itemProp="description"
              sx={{
                mb: { xs: 2.5, md: 4 },
                fontSize: { xs: "0.92rem", sm: "1rem", md: "1.2rem" },
                lineHeight: 1.6,
              }}
            >
              Create a{" "}
              <Box component="strong" sx={{ color: "text.primary" }}>
                job-winning resume
              </Box>{" "}
              in minutes — no design skills needed.{" "}
              <Box component="strong" sx={{ color: "text.primary" }}>
                100% free, ATS-friendly
              </Box>
              , and built for your success.
            </Typography>

            {/* CTA Buttons - Equal size side by side with reduced font */}
            <Box
              sx={{
                mb: { xs: 3, md: 5 },
                display: "flex",
                flexDirection: "row", // Always in single line
                justifyContent: { xs: "center", md: "flex-start" },
                gap: 1.5,
                flexWrap: "nowrap", // Prevent wrapping
              }}
            >
              {/* Create My Resume Now Button */}
              <Button
                variant="contained"
                size="large"
                component="a"
                href="/steps"
                aria-label="Create My Resume Now – free online resume builder"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/steps");
                }}
                sx={{
                  background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                  color: "#fff",
                  fontWeight: "bold",
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 1, sm: 1.2, md: 1.5 },
                  borderRadius: "40px",
                  flex: 1,
                  minWidth: 0,
                  width: "auto",
                  textTransform: "none",
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.85rem" }, // Reduced font size
                  boxShadow: "0 8px 16px rgba(106, 17, 203, 0.3)",
                  textDecoration: "none",
                  whiteSpace: "nowrap", // Prevent text wrapping
                  "&:hover": {
                    background: "linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)",
                    boxShadow: "0 12px 20px rgba(106, 17, 203, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Create My Resume Now
              </Button>

              {/* AI Analyzer Button - Same size as first button */}
              <Button
                variant="contained"
                size="large"
                component="a"
                href="/ai-analyzer"
                aria-label="Check your ATS score with AI Analyzer"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/ai-analyzer");
                }}
                sx={{
                  background: "linear-gradient(135deg, #0288D1 0%, #002B5B 100%)",
                  color: "#fff",
                  fontWeight: "bold",
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 1, sm: 1.2, md: 1.5 },
                  borderRadius: "40px",
                  flex: 1,
                  minWidth: 0,
                  width: "auto",
                  textTransform: "none",
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.85rem" }, // Reduced font size
                  boxShadow: "0 8px 16px rgba(2, 136, 209, 0.3)",
                  textDecoration: "none",
                  whiteSpace: "nowrap", // Prevent text wrapping
                  "&:hover": {
                    background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                    boxShadow: "0 12px 20px rgba(2, 136, 209, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Check ATS Score with AI
              </Button>
            </Box>

            {/* Stats — ✅ SEO: schema.org/ItemList */}
            <Box
              component="ul"
              aria-label="Resume builder statistics"
              itemScope
              itemType="https://schema.org/ItemList"
              sx={{
                display: "flex",
                gap: { xs: 5, sm: 6 },
                justifyContent: { xs: "center", md: "flex-start" },
                listStyle: "none",
                m: 0,
                p: 0,
                mb: { xs: 2, md: 5 },
              }}
            >
              {[
                {
                  stat: "↑ 38%",
                  label: "more interviews",
                  itemprop: "38% more interview calls for resume builder users",
                },
                {
                  stat: "↑ 23%",
                  label: "more likely to get a job offer",
                  itemprop: "23% higher job offer rate with professional resume",
                },
              ].map(({ stat, label, itemprop }) => (
                <Box
                  component="li"
                  key={stat}
                  itemScope
                  itemType="https://schema.org/ListItem"
                  itemProp="itemListElement"
                >
                  <meta itemProp="name" content={itemprop} />
                  <Typography
                    aria-label={itemprop}
                    sx={{
                      color: "#6a11cb",
                      fontWeight: "bold",
                      fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.2rem" },
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {stat}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              component="p"
              sx={{
                textAlign: { xs: "center", md: "left" },
                color: "text.secondary",
                fontSize: { xs: "0.85rem", sm: "1rem" },
                fontWeight: 500,
              }}
            >
              Subscribers have been hired by: *
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeroSection;